import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { NoticeBox } from "@/components/NoticeBox";
import { useShareGate } from "@/hooks/useShareGate";
import { downloadBlob } from "@/components/ToolLayout";
import { exportKey } from "@/lib/exportCharge";
import { buildGrid, buildIcs, clockChangeText, friendlyZone, nextClockChange, overlapText, shareQuery, DEFAULT_WORK, type WorkHours } from "./tzPlan";

const HOURS = Array.from({ length: 25 }, (_, i) => i);

/**
 * Meeting planner under the converter (B-0117): an hour-by-hour grid of the day in every chosen zone with working hours shaded,
 * click an hour to pick it, the next clock change per zone, a share link and a calendar (.ics) file. All times come from the
 * browser's Intl time zone data (tzPlan.ts); nothing is stored or sent, and the share link holds only zones, a time and hours.
 * Loaded on demand (React.lazy).
 */
export default function TimezonePlanner({ zones, time, utc, work, onWork, onPick }: {
  zones: string[]; time: string; utc: number | null; work: WorkHours; onWork: (w: WorkHours) => void; onPick: (time: string) => void;
}) {
  const { gateShare } = useShareGate();
  const [title, setTitle] = useState("");
  const [minutes, setMinutes] = useState(60);
  const day = useMemo(() => {
    const m = /^(\d{4})-(\d{2})-(\d{2})T/.exec(time);
    return m ? { y: Number(m[1]), mo: Number(m[2]), d: Number(m[3]) } : null;
  }, [time]);
  const grid = useMemo(() => (day ? buildGrid(zones, day, work) : null), [zones, day, work]);
  const now = useMemo(() => Date.now(), []);
  const changes = useMemo(() => zones.map((z) => ({ zone: z, change: nextClockChange(z, now) })), [zones, now]);
  const HOUR = 3600000;
  const selected = grid && utc !== null ? grid.columns.findIndex((c) => utc >= c.utc && utc < c.utc + HOUR) : -1;

  const query = shareQuery({ zones, time, work });
  const link = typeof window !== "undefined" ? `${window.location.origin}${window.location.pathname}?${query}` : `?${query}`;
  const key = exportKey({ zones, time, work });

  const copyLink = () => gateShare(async () => {
    try { await navigator.clipboard.writeText(link); toast.success("Share link copied"); return true; }
    catch { toast.error("Could not copy. Select the link and copy it by hand."); return false; }
  }, exportKey({ kind: "link", key }));
  const downloadIcs = () => void gateShare(async () => {
    if (utc === null) return false;
    const ics = buildIcs({ title: title.trim(), startUtc: utc, minutes, zones, now: Date.now(), link });
    downloadBlob(new Blob([ics], { type: "text/calendar;charset=utf-8" }), "meeting.ics", { free: true });
    return true;
  }, exportKey({ kind: "ics", key, title, minutes }));

  if (!grid || zones.length < 1) return null;
  const refFriendly = friendlyZone(grid.columns[0]?.utc ?? now, zones[0]);

  return (
    <section className="mt-8 space-y-4" aria-label="Meeting planner" data-testid="tz-planner">
      <div>
        <h2 className="text-base font-semibold">Meeting planner</h2>
        <p className="text-xs text-muted-foreground">The day of your chosen time, hour by hour, in every zone above. Shaded hours are inside working hours on that zone's own clock. Pick an hour to use it.</p>
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <Label htmlFor="tz-work-start" className="text-xs">Work starts</Label>
          <select id="tz-work-start" value={work.start} onChange={(e) => onWork({ start: Number(e.target.value), end: Math.max(work.end, Number(e.target.value) + 1) })} className="block border rounded-md px-2 py-1.5 bg-background text-sm">
            {HOURS.slice(0, 24).map((h) => <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>)}
          </select>
        </div>
        <div>
          <Label htmlFor="tz-work-end" className="text-xs">Work ends</Label>
          <select id="tz-work-end" value={work.end} onChange={(e) => onWork({ start: Math.min(work.start, Number(e.target.value) - 1), end: Number(e.target.value) })} className="block border rounded-md px-2 py-1.5 bg-background text-sm">
            {HOURS.slice(1).map((h) => <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>)}
          </select>
        </div>
        <Button type="button" size="sm" variant="ghost" onClick={() => onWork(DEFAULT_WORK)}>Reset to 09:00 to 17:00</Button>
      </div>

      <div className="overflow-x-auto rounded-lg border" tabIndex={0} aria-label="Hour-by-hour time zone grid, scrollable">
        <table className="text-xs border-collapse" data-testid="tz-grid">
          <caption className="sr-only">Local time in each zone for every hour of {grid.columns[0]?.cells[0].date}, read on the clock in {refFriendly.title}</caption>
          <thead>
            <tr>
              <th scope="col" className="sticky left-0 z-10 bg-background p-2 text-left font-medium min-w-[9rem]">Zone</th>
              {grid.columns.map((c, i) => (
                <th key={c.utc} scope="col" className="p-0 font-normal">
                  <button type="button" onClick={() => onPick(`${c.cells[0].date}T${c.cells[0].local}`)} aria-pressed={i === selected} aria-label={`Use ${c.cells[0].local} in ${refFriendly.title}`}
                    className={`block w-full min-w-[3rem] px-1 py-1.5 border-l text-center ${i === selected ? "bg-primary text-primary-foreground font-semibold" : "hover:bg-primary/10"}`}>
                    {c.cells[0].local}{c.cells[0].changed ? <span title="The clocks change at this hour"> *</span> : null}
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {zones.map((z, zi) => (
              <tr key={z} className="border-t">
                <th scope="row" className="sticky left-0 z-10 bg-background p-2 text-left font-normal min-w-[9rem]">
                  <span className="font-medium">{friendlyZone(grid.columns[0].utc, z).title}</span>
                  <span className="block text-muted-foreground">{z}</span>
                </th>
                {grid.columns.map((c, i) => {
                  const cell = c.cells[zi];
                  return (
                    <td key={c.utc} className={`border-l px-1 py-1.5 text-center ${cell.working ? "bg-primary/15 font-semibold" : "text-muted-foreground"} ${i === selected ? "ring-2 ring-inset ring-primary" : ""}`}
                      title={`${cell.local}${cell.dayShift ? (cell.dayShift > 0 ? " next day" : " previous day") : ""}${cell.working ? ", working hours" : ", outside working hours"}${cell.changed ? ", clocks change here" : ""}`}>
                      {cell.local}
                      {cell.dayShift !== 0 && <span className="block text-[10px]">{cell.dayShift > 0 ? "+1 day" : "-1 day"}</span>}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-muted-foreground">Shaded and bold: working hours ({String(work.start).padStart(2, "0")}:00 to {String(work.end).padStart(2, "0")}:00). Columns are hours on the clock in {refFriendly.title}; * marks an hour where that clock changes, so the day can have 23 or 25 columns.</p>
      <NoticeBox role="status" className="text-sm" testId="tz-overlap">{overlapText(grid, work)}</NoticeBox>

      <div className="space-y-1" data-testid="tz-changes">
        <p className="text-sm font-medium">Next clock change</p>
        <ul className="text-sm space-y-1">
          {changes.map(({ zone, change }) => (
            <li key={zone}><strong>{friendlyZone(now, zone).title}:</strong> {change ? clockChangeText(zone, change, now) : "no clock change in the next 13 months"}</li>
          ))}
        </ul>
        <p className="text-xs text-muted-foreground">From your device's time zone data, so a change a government announces later shows up when your browser updates.</p>
      </div>

      <div className="space-y-3 rounded-lg border p-4">
        <p className="text-sm font-medium">Share or add to your calendar</p>
        <div>
          <Label htmlFor="tz-link" className="text-xs">Share link (zones, time and working hours only)</Label>
          <Input id="tz-link" readOnly value={link} onFocus={(e) => e.currentTarget.select()} className="font-mono text-xs" />
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[10rem] flex-1">
            <Label htmlFor="tz-ics-title" className="text-xs">Event title</Label>
            <Input id="tz-ics-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Meeting" maxLength={120} />
          </div>
          <div>
            <Label htmlFor="tz-ics-len" className="text-xs">Length</Label>
            <select id="tz-ics-len" value={minutes} onChange={(e) => setMinutes(Number(e.target.value))} className="block border rounded-md px-2 py-1.5 bg-background text-sm">
              {[15, 30, 45, 60, 90, 120].map((m) => <option key={m} value={m}>{m} minutes</option>)}
            </select>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" size="sm" onClick={copyLink}>Copy share link</Button>
          <Button type="button" variant="outline" size="sm" onClick={downloadIcs} disabled={utc === null}>Download calendar file (.ics)</Button>
        </div>
        <p className="text-xs text-muted-foreground">Nothing is saved or uploaded. The calendar file holds one event at the exact moment (in UTC, so your calendar shows it in your own zone) and lists the time in each zone. Copying the link or downloading the file takes one trial use.</p>
      </div>
    </section>
  );
}
